import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import moment from 'moment';
import {
    MDBBtn, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter
} from 'mdb-react-ui-kit';
import classroomService from '../../../services/classroomService';
import types from '../../../services/types';
import utils from '../../../utils/generalFunctions';
import Loading from '../../../components/Loading';
import HtmlEditor from '../../../components/HtmlEditor';
import ClassroomIntervalForm from '../../../components/Forms/ClassroomIntervalForm';
import ClassroomSectionForm from '../../../components/Forms/ClassroomSectionForm';
import ClassroomResourceForm from '../../../components/Forms/ClassroomResourceForm';
import { IntervalSelector, SectionCard, enrichResources } from './ClassroomShared';
import '../AulaVirtual.css';

// startDate/endDate son columnas DATE (sin hora): se formatean como fecha
// simple, mismo formato que usa el resto de la app para rangos de fecha
// (ver p. ej. Cartera/descuentoestudiantes.js) — sin am/pm porque no hay
// una hora real detrás, mostrarla sería un "12:00 AM" inventado.
const formatearFecha = (fecha) => (fecha ? moment(fecha).format('DD-MM-YYYY') : '...');

// Panel de gestión del docente: MISMO layout que ve el estudiante (selector
// de intervalos arriba, secciones del intervalo elegido abajo, con su
// contenido real renderizado) — solo se le suman los botones de
// administración (visible/oculto, editar, eliminar, agregar). El docente
// siempre ve todo, visible u oculto para el estudiante (ver DB/README.md §5).
// Acepta `classroomId` por prop para poder incrustarse dentro de otra
// página (p. ej. /auladash, que carga el aula debajo de su buscador sin
// cambiar de ruta); si no llega por prop, lo toma de la URL como siempre.
const ClassroomManage = ({ classroomId: classroomIdProp }) => {
    const params = useParams();
    const classroomId = classroomIdProp || params.classroomId;
    const [classroom, setClassroom] = useState(null);
    const [intervals, setIntervals] = useState([]);
    const [seleccionadoId, setSeleccionadoId] = useState(null);
    const [enriched, setEnriched] = useState({}); // { [resourceId]: recurso con .document/.video }
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // { type, data, parentId }
    const [showModal, setShowModal] = useState(false); // controla MDBModal por separado del contenido
    // Se incrementa cada vez que se abre el modal y se usa como `key` de los
    // formularios de abajo: fuerza que React los desmonte y monte de nuevo
    // en cada apertura, en vez de reutilizar la misma instancia. Sin esto,
    // el formulario (que solo lee sus props iniciales una vez con useState)
    // se queda con los datos del primer intervalo/sección/recurso que
    // editó y no se actualiza aunque `modal.data` cambie — así se veía
    // siempre el mismo registro sin importar cuál se seleccionara.
    const [modalToken, setModalToken] = useState(0);

    const user = utils.getUserInformation();

    const load = useCallback(async () => {
        try {
            const cl = await classroomService.getClassroom(classroomId);
            setClassroom(cl);

            const ivs = await classroomService.getIntervals(classroomId, 'teacher');
            const ivsConSecciones = await Promise.all(
                (ivs || []).map(async (iv) => {
                    const secciones = await classroomService.getSections(iv.intervalId, 'teacher');
                    const seccionesConRecursos = await Promise.all(
                        (secciones || []).map(async (sec) => {
                            const recursos = await classroomService.getResources(sec.sectionId, 'teacher');
                            return { ...sec, resources: recursos || [] };
                        })
                    );
                    return { ...iv, sections: seccionesConRecursos };
                })
            );
            setIntervals(ivsConSecciones.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)));
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    }, [classroomId]);

    useEffect(() => {
        load();
    }, [load]);

    // Mantiene siempre un intervalo válido seleccionado: el primero al
    // cargar, o se re-elige si el que estaba seleccionado se eliminó.
    // Elegir uno nuevo no toca los demás (no depende de recargar nada).
    useEffect(() => {
        if (intervals.length === 0) {
            setSeleccionadoId(null);
            return;
        }
        if (!intervals.some((iv) => iv.intervalId === seleccionadoId)) {
            setSeleccionadoId(intervals[0].intervalId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intervals]);

    const seleccionado = intervals.find((iv) => iv.intervalId === seleccionadoId) || null;

    // Trae document/video solo para los recursos del intervalo que se está
    // mostrando (no de toda el aula) y los cachea por resourceId — evita
    // repetir la cascada de peticiones que ya se corrigió en el resto del
    // módulo, y no se re-pide nada que ya esté en caché.
    useEffect(() => {
        if (!seleccionado) return;
        const pendientes = seleccionado.sections
            .flatMap((s) => s.resources)
            .filter((r) => !enriched[r.resourceId] && (
                (r.resourceTypeId === types.classroomResourceTypes.VIDEO && r.videoId) ||
                (r.resourceTypeId === types.classroomResourceTypes.FILE && r.documentId)
            ));
        if (pendientes.length === 0) return;
        enrichResources(pendientes).then((enriquecidos) => {
            setEnriched((prev) => {
                const copia = { ...prev };
                enriquecidos.forEach((r) => {
                    // Solo se cachea el dato adicional (document/video),
                    // NUNCA el recurso completo: si se cacheara todo el
                    // objeto, al mezclarlo más abajo su isVisible (una foto
                    // del momento en que se enriqueció) taparía el valor
                    // fresco cada vez que se cambia visibilidad después.
                    copia[r.resourceId] = { document: r.document, video: r.video };
                });
                return copia;
            });
        });
    }, [seleccionadoId, intervals]); // eslint-disable-line react-hooks/exhaustive-deps

    const openModal = (config) => {
        setModal(config);
        setModalToken((t) => t + 1);
        setShowModal(true);
    };

    // Al guardar desde el modal, el backend ya devuelve el registro
    // confirmado en la base de datos (con su id real y valores por
    // defecto aplicados) — se usa esa respuesta para insertar/reemplazar
    // solo el nodo afectado en el estado local, en vez de recargar todo
    // el árbol (aula -> intervalos -> secciones -> recursos) por un solo
    // cambio. No es una actualización optimista: solo se actualiza el
    // estado local después de que el servidor confirmó el guardado.
    const closeIntervalModal = async (savedInterval) => {
        setShowModal(false);
        if (!savedInterval || !savedInterval.intervalId) return;

        const existente = intervals.find((iv) => iv.intervalId === savedInterval.intervalId);
        if (existente) {
            setIntervals((prev) => prev
                .map((iv) => (iv.intervalId === savedInterval.intervalId ? { ...savedInterval, sections: iv.sections } : iv))
                .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)));
            return;
        }

        // Intervalo nuevo: siempre trae sus 4 secciones por defecto ya
        // sembradas por el backend; se piden una sola vez (no todo el árbol).
        const sections = ((await classroomService.getSections(savedInterval.intervalId, 'teacher')) || [])
            .map((s) => ({ ...s, resources: [] }));
        setIntervals((prev) => [...prev, { ...savedInterval, sections }].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)));
        setSeleccionadoId(savedInterval.intervalId);
    };

    const closeSectionModal = (savedSection) => {
        setShowModal(false);
        if (!savedSection || !savedSection.intervalId) return;
        setIntervals((prev) => prev.map((iv) => {
            if (iv.intervalId !== savedSection.intervalId) return iv;
            const existente = iv.sections.some((s) => s.sectionId === savedSection.sectionId);
            const sections = existente
                ? iv.sections.map((s) => (s.sectionId === savedSection.sectionId ? { ...savedSection, resources: s.resources } : s))
                : [...iv.sections, { ...savedSection, resources: [] }];
            return { ...iv, sections: sections.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)) };
        }));
    };

    const closeResourceModal = (savedResource) => {
        setShowModal(false);
        if (!savedResource || !savedResource.sectionId) return;
        setIntervals((prev) => prev.map((iv) => ({
            ...iv,
            sections: iv.sections.map((sec) => {
                if (sec.sectionId !== savedResource.sectionId) return sec;
                const existente = sec.resources.some((r) => r.resourceId === savedResource.resourceId);
                const resources = existente
                    ? sec.resources.map((r) => (r.resourceId === savedResource.resourceId ? savedResource : r))
                    : [...sec.resources, savedResource];
                return { ...sec, resources: resources.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)) };
            })
        })));
    };

    // Actualización confirmada: cambia el estado local recién después de
    // que el servidor confirma, evitando recargar todo el árbol
    // (aula -> intervalos -> secciones -> recursos) por un solo booleano.
    const setIntervalInState = (intervalId, cambios) => {
        setIntervals((prev) => prev.map((iv) => (iv.intervalId === intervalId ? { ...iv, ...cambios } : iv)));
    };

    const setSectionInState = (intervalId, sectionId, cambios) => {
        setIntervals((prev) => prev.map((iv) => (
            iv.intervalId !== intervalId ? iv : {
                ...iv,
                sections: iv.sections.map((sec) => (sec.sectionId === sectionId ? { ...sec, ...cambios } : sec))
            }
        )));
    };

    const setResourceInState = (sectionId, resourceId, cambios) => {
        setIntervals((prev) => prev.map((iv) => ({
            ...iv,
            sections: iv.sections.map((sec) => (
                sec.sectionId !== sectionId ? sec : {
                    ...sec,
                    resources: sec.resources.map((r) => (r.resourceId === resourceId ? { ...r, ...cambios } : r))
                }
            ))
        })));
    };

    // Evita la condición de carrera de clics repetidos: mientras haya una
    // petición de visibilidad en curso para un ítem, se ignoran nuevos
    // clics sobre ese mismo ítem (si no, dos peticiones en vuelo pueden
    // resolverse fuera de orden y la que llega después "gana" en la base
    // de datos aunque no sea la última que el usuario pidió). El ref se
    // consulta de forma síncrona (no depende de que React ya haya
    // re-renderizado); el estado espejo solo es para deshabilitar el botón.
    const pendingVisibilityRef = useRef(new Set());
    const [pendingVisibility, setPendingVisibility] = useState(new Set());

    const withVisibilityGuard = async (key, run) => {
        if (pendingVisibilityRef.current.has(key)) return;
        pendingVisibilityRef.current.add(key);
        setPendingVisibility(new Set(pendingVisibilityRef.current));
        try {
            await run();
        } finally {
            pendingVisibilityRef.current.delete(key);
            setPendingVisibility(new Set(pendingVisibilityRef.current));
        }
    };

    const toggleIntervalVisible = (interval) => withVisibilityGuard(`interval-${interval.intervalId}`, async () => {
        const nuevoValor = !interval.isVisible;
        setIntervalInState(interval.intervalId, { isVisible: nuevoValor });
        try {
            await classroomService.setIntervalVisibility(interval.intervalId, nuevoValor, user?.userId);
        } catch (error) {
            setIntervalInState(interval.intervalId, { isVisible: interval.isVisible });
            Swal.fire('Ha ocurrido un error', 'No se pudo actualizar la visibilidad.', 'error');
        }
    });

    const toggleSectionVisible = (section) => withVisibilityGuard(`section-${section.sectionId}`, async () => {
        const nuevoValor = !section.isVisible;
        setSectionInState(section.intervalId, section.sectionId, { isVisible: nuevoValor });
        try {
            await classroomService.setSectionVisibility(section.sectionId, nuevoValor, user?.userId);
        } catch (error) {
            setSectionInState(section.intervalId, section.sectionId, { isVisible: section.isVisible });
            Swal.fire('Ha ocurrido un error', 'No se pudo actualizar la visibilidad.', 'error');
        }
    });

    const toggleResourceVisible = (resource) => withVisibilityGuard(`resource-${resource.resourceId}`, async () => {
        const nuevoValor = !resource.isVisible;
        setResourceInState(resource.sectionId, resource.resourceId, { isVisible: nuevoValor });
        try {
            await classroomService.setResourceVisibility(resource.resourceId, nuevoValor, user?.userId);
        } catch (error) {
            setResourceInState(resource.sectionId, resource.resourceId, { isVisible: resource.isVisible });
            Swal.fire('Ha ocurrido un error', 'No se pudo actualizar la visibilidad.', 'error');
        }
    });

    // accion() debe confirmar el borrado en el backend antes de quitar el
    // nodo del estado local (nada de suponerlo antes de la confirmación).
    const confirmarYEliminar = (titulo, accion) => {
        Swal.fire({
            title: titulo,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--ge-primary, #4f8cff)',
            cancelButtonColor: 'var(--ge-danger, #e35d6a)',
            confirmButtonText: 'Sí, eliminar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await accion();
                    Swal.fire('Eliminado', '', 'success');
                } catch (error) {
                    Swal.fire('Ha ocurrido un error', 'Por favor intentelo más tarde', 'error');
                }
            }
        });
    };

    const deleteInterval = (interval) =>
        confirmarYEliminar('¿Eliminar este intervalo y todo su contenido?', async () => {
            await classroomService.deleteInterval(interval.intervalId);
            setIntervals((prev) => prev.filter((iv) => iv.intervalId !== interval.intervalId));
        });

    const deleteSection = (section) =>
        confirmarYEliminar('¿Eliminar esta sección y todo su contenido?', async () => {
            await classroomService.deleteSection(section.sectionId);
            setIntervals((prev) => prev.map((iv) => (
                iv.intervalId !== section.intervalId ? iv : { ...iv, sections: iv.sections.filter((s) => s.sectionId !== section.sectionId) }
            )));
        });

    const deleteResource = (resource) =>
        confirmarYEliminar('¿Quitar este recurso de la sección?', async () => {
            await classroomService.deleteResource(resource.resourceId);
            setIntervals((prev) => prev.map((iv) => ({
                ...iv,
                sections: iv.sections.map((sec) => (
                    sec.sectionId !== resource.sectionId ? sec : { ...sec, resources: sec.resources.filter((r) => r.resourceId !== resource.resourceId) }
                ))
            })));
        });

    const guardarBienvenida = async (welcomeMessageHtml) => {
        const actualizado = await classroomService.updateClassroom({ ...classroom, welcomeMessageHtml, modificationUser: user?.userId });
        setClassroom(actualizado);
        setShowModal(false);
    };

    if (loading) return <Loading />;

    if (!classroom || !classroom.classroomId) {
        return <div className="alert alert-warning av-module">No encontramos esta aula.</div>;
    }

    // Mezcla los datos de document/video (cacheados en `enriched`) con los
    // recursos del intervalo seleccionado, para que se vean con el mismo
    // contenido real que ve el estudiante.
    const seccionesVisibles = seleccionado
        ? seleccionado.sections.map((sec) => ({
            ...sec,
            resources: sec.resources.map((r) => (enriched[r.resourceId] ? { ...r, ...enriched[r.resourceId] } : r))
        }))
        : [];

    return (
        <div className="container-fluid p-0 av-module">
            <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                <div>
                    <h1 className="h3 mb-0">{classroom.schoolSubjectName}</h1>
                    <p className="text-muted mb-0">{classroom.courseName} · {classroom.schoolYear}</p>
                </div>
                <div className="btn-group">
                    <button className="btn btn-outline-primary" onClick={() => openModal({ type: 'welcome' })}>
                        <i className="fas fa-pen me-1"></i> Mensaje de bienvenida
                    </button>
                    <button className="btn btn-primary" onClick={() => openModal({ type: 'interval', data: null })}>
                        <i className="fas fa-circle-plus me-1"></i> Agregar intervalo
                    </button>
                </div>
            </div>

            {intervals.length === 0 ? (
                <div className="alert alert-info">Todavía no hay intervalos. Crea el primero para empezar a organizar el aula.</div>
            ) : (
                <>
                    <IntervalSelector intervals={intervals} seleccionado={seleccionado} onSelect={(iv) => setSeleccionadoId(iv.intervalId)} admin />

                    {seleccionado && (
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <div>
                                <strong>{seleccionado.name}</strong>{' '}
                                {(seleccionado.startDate || seleccionado.endDate) && (
                                    <span className="text-muted small">
                                        ({formatearFecha(seleccionado.startDate)} - {formatearFecha(seleccionado.endDate)})
                                    </span>
                                )}
                                <span className={`badge ms-2 ${seleccionado.isVisible ? 'bg-success' : 'bg-secondary'}`}>
                                    {seleccionado.isVisible ? 'Visible' : 'Oculto'}
                                </span>
                            </div>
                            <div className="btn-group btn-group-sm">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => toggleIntervalVisible(seleccionado)}
                                    disabled={pendingVisibility.has(`interval-${seleccionado.intervalId}`)}
                                    title="Mostrar/ocultar"
                                >
                                    <i className={`fas ${pendingVisibility.has(`interval-${seleccionado.intervalId}`) ? 'fa-spinner fa-spin' : seleccionado.isVisible ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                                <button className="btn btn-outline-info" onClick={() => openModal({ type: 'interval', data: seleccionado })} title="Editar">
                                    <i className="fas fa-pencil-alt"></i>
                                </button>
                                <button className="btn btn-outline-danger" onClick={() => deleteInterval(seleccionado)} title="Eliminar">
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {seccionesVisibles.map((section) => (
                        <SectionCard
                            key={section.sectionId}
                            section={section}
                            admin={{
                                pending: pendingVisibility.has(`section-${section.sectionId}`),
                                onToggleVisible: () => toggleSectionVisible(section),
                                onEdit: () => openModal({ type: 'section', data: section }),
                                onDelete: () => deleteSection(section),
                                onAddResource: () => openModal({ type: 'resource', data: null, parentId: section.sectionId }),
                                resourceAdmin: (resource) => ({
                                    pending: pendingVisibility.has(`resource-${resource.resourceId}`),
                                    onToggleVisible: () => toggleResourceVisible(resource),
                                    onEdit: () => openModal({ type: 'resource', data: resource }),
                                    onDelete: () => deleteResource(resource)
                                })
                            }}
                        />
                    ))}

                    {seleccionado && (
                        <button className="btn btn-sm btn-outline-primary" onClick={() => openModal({ type: 'section', data: null, parentId: seleccionado.intervalId })}>
                            <i className="fas fa-circle-plus me-1"></i> Agregar sección
                        </button>
                    )}
                </>
            )}

            <MDBModal tabIndex="-1" show={showModal} setShow={setShowModal}>
                <MDBModalDialog className="modal-dialog modal-lg">
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>
                                {modal?.type === 'welcome' && 'Mensaje de bienvenida del aula'}
                                {modal?.type === 'interval' && (modal.data ? 'Editar intervalo' : 'Nuevo intervalo')}
                                {modal?.type === 'section' && (modal.data ? 'Editar sección' : 'Nueva sección')}
                                {modal?.type === 'resource' && (modal.data ? 'Editar recurso' : 'Nuevo recurso')}
                            </MDBModalTitle>
                            <MDBBtn type="button" className="btn-close" color="none" onClick={() => setShowModal(false)}></MDBBtn>
                        </MDBModalHeader>
                        <MDBModalBody>
                            {modal?.type === 'welcome' && (
                                <WelcomeEditor initial={classroom.welcomeMessageHtml} onSave={guardarBienvenida} />
                            )}
                            {modal?.type === 'interval' && (
                                <ClassroomIntervalForm key={modalToken} classroomId={Number(classroomId)} interval={modal.data} onClose={closeIntervalModal} />
                            )}
                            {modal?.type === 'section' && (
                                <ClassroomSectionForm key={modalToken} intervalId={modal.data?.intervalId || modal.parentId} section={modal.data} onClose={closeSectionModal} />
                            )}
                            {modal?.type === 'resource' && (
                                <ClassroomResourceForm
                                    key={modalToken}
                                    classroomId={Number(classroomId)}
                                    sectionId={modal.data?.sectionId || modal.parentId}
                                    resource={modal.data}
                                    onClose={closeResourceModal}
                                />
                            )}
                        </MDBModalBody>
                        <MDBModalFooter>
                            <button type="button" className="btn btn-primary" onClick={() => setShowModal(false)}>Cerrar</button>
                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        </div>
    );
};

const WelcomeEditor = ({ initial, onSave }) => {
    const [value, setValue] = useState(initial || '');
    return (
        <div>
            <HtmlEditor value={value} onChange={setValue} />
            <button className="btn btn-primary mt-3" onClick={() => onSave(value)}>Guardar</button>
        </div>
    );
};

export default ClassroomManage;
